import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';

import { useBoardSwitcher } from 'app/src/components/BoardSwitcher/useBoardSwitcher';
import { useIsEligibleForPlannerDiscoverySpotlight } from 'app/src/components/PlannerDiscoverySpotlight/useIsEligibleForPlannerDiscoverySpotlight';
import { BoardSwitcherButtonSpotlight } from './BoardSwitcherButtonSpotlight';
import {
  BOARD_SWITCHER_SPOTLIGHT_MESSAGE_ID,
  ISLAND_NAV_SPOTLIGHT_MESSAGE_ID,
} from './PersonalProductivityOnboarding.constants';

/**
 * This hook is used to determine if the board switcher spotlight and pulse should be shown. If the user is in the PP cohort,
 * and has the spotlight pulse gate enabled, and has dismissed the island nav spotlight, and has not dismissed the board switcher spotlight,
 * and is not eligible for the planner discovery spotlight.
 *
 * It also returns a function to render the spotlight, in the pattern of the personal productivity spotlight tour.
 * @returns {
 *  renderSingleBoardSwitcherSpotlight: () => React.ReactNode,
 *  shouldShowSpotlight: boolean,
 * }
 */
export const useBoardSwitcherButtonPulseAndSpotlight = () => {
  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  //we use recentBoards to determine if the user has > 1 board, because when they create a board they auto-visit it
  //and that will add it to their recent boards
  const { recentBoards } = useBoardSwitcher();
  const numBoards = recentBoards.length;

  const {
    value: isPersonalProdEnabled,
    loading: isPersonalProdEnabledLoading,
  } = useFeatureGate('trello_personal_productivity_release');

  const { value: isSpotlightEnabled, loading: isSpotlightEnabledLoading } =
    useFeatureGate('phx_board_switcher_spotlight_pulse');

  const { isEligible: isEligibleForPlannerDiscoverySpotlight } =
    useIsEligibleForPlannerDiscoverySpotlight();

  const renderSingleBoardSwitcherSpotlight = () => {
    return <BoardSwitcherButtonSpotlight />;
  };

  const shouldShowSpotlight =
    isPersonalProdEnabled &&
    !isPersonalProdEnabledLoading &&
    isSpotlightEnabled &&
    !isSpotlightEnabledLoading &&
    numBoards > 1 &&
    isOneTimeMessageDismissed(ISLAND_NAV_SPOTLIGHT_MESSAGE_ID) &&
    !isOneTimeMessageDismissed(BOARD_SWITCHER_SPOTLIGHT_MESSAGE_ID) &&
    !isEligibleForPlannerDiscoverySpotlight;

  return {
    shouldShowSpotlight,
    renderSingleBoardSwitcherSpotlight,
  };
};
